/**
 * GroupPicker: choose one or more active groups of the network (plan
 * docs/plans/lms-plan.md 8.2 "groups (profile or adhoc)", 5.3; WP-43; reused
 * by WP-45 and WP-46).
 *
 * Props (stable):
 *   value      string[]                                    the chosen group ids
 *   onChange   (ids: string[], groups: GroupListItem[]) => void   groups = the chosen ones the picker has loaded
 *   kind?      'profile' | 'adhoc'                          only that kind (default both)
 *   label?     string                                      the fieldset legend (default "Groups")
 *   disabled?  boolean
 *
 * Reads GET /admin/groups (api/groups.ts listGroups; active groups only, by
 * name, 50 a page with "Show more"). Each group is a checkbox row with its
 * kind and member count, data-testid="group-option-<id>" on the checkbox.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { secondaryBtnCls } from '../../../ui/styles';
import { apiErrorMessage } from '../../api/client';
import { listGroups } from '../../api/groups';
import type { GroupKind, GroupListItem } from '../../api/groups';
import { checkboxCls, checkRowCls, ErrorNote, metaCls, microLabelCls, Pill, peopleCount } from './shared';

export interface GroupPickerProps {
  value: string[];
  onChange: (ids: string[], groups: GroupListItem[]) => void;
  kind?: GroupKind;
  label?: string;
  disabled?: boolean;
}

const PAGE = 50;

export function GroupPicker({ value, onChange, kind, label = 'Groups', disabled = false }: GroupPickerProps) {
  const [groups, setGroups] = useState<GroupListItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const request = useRef(0);

  const load = useCallback(
    (offset: number) => {
      const id = ++request.current;
      setLoading(true);
      setError(null);
      listGroups({ kind: kind ?? null, limit: PAGE, offset })
        .then((page) => {
          if (id !== request.current) return;
          const items = Array.isArray(page.items) ? page.items : [];
          setGroups((prev) => (offset === 0 ? items : [...prev, ...items.filter((g) => !prev.some((p) => p.id === g.id))]));
          setHasMore(Boolean(page.page?.hasMore));
        })
        .catch((err) => {
          if (id !== request.current) return;
          setError(apiErrorMessage(err, 'Could not load the groups.'));
        })
        .finally(() => {
          if (id === request.current) setLoading(false);
        });
    },
    [kind]
  );

  useEffect(() => {
    load(0);
  }, [load]);

  const toggle = (id: string) => {
    const next = value.includes(id) ? value.filter((v) => v !== id) : [...value, id];
    onChange(
      next,
      groups.filter((g) => next.includes(g.id))
    );
  };

  return (
    <fieldset data-testid="group-picker" disabled={disabled}>
      <legend className={`${microLabelCls} mb-2`}>{label}</legend>
      {error && (
        <ErrorNote>
          {error}{' '}
          <button type="button" className="font-medium underline" onClick={() => load(0)}>
            Retry
          </button>
        </ErrorNote>
      )}
      {!error && !loading && groups.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">No active groups yet. Create one under Groups.</p>
      )}
      {groups.length > 0 && (
        <ul className="max-h-64 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
          {groups.map((g) => (
            <li key={g.id}>
              <label className={checkRowCls}>
                <input
                  type="checkbox"
                  className={checkboxCls}
                  checked={value.includes(g.id)}
                  onChange={() => toggle(g.id)}
                  data-testid={`group-option-${g.id}`}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-gray-900 dark:text-gray-100">{g.name}</span>
                  <span className={metaCls}>{peopleCount(g.member_count ?? 0)}</span>
                </span>
                <Pill tone={g.kind === 'profile' ? 'primary' : 'gray'}>{g.kind === 'profile' ? 'Profile' : 'Saved audience'}</Pill>
              </label>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-2 flex items-center gap-2">
        {loading && <Loader2 aria-label="Loading groups" className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" />}
        {hasMore && !loading && (
          <button type="button" className={secondaryBtnCls} onClick={() => load(groups.length)}>
            Show more groups
          </button>
        )}
      </div>
    </fieldset>
  );
}

export default GroupPicker;
